import Switch from '@mui/material/Switch';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Roles } from 'src/enums';
import { useGetCardsEnabledQuery, useUpdateCardsEnabledMutation } from 'src/redux/services/cards';
import { RootState } from 'src/redux/store';
import { TUserSlice } from 'src/redux/userSlice';
import { CustomTypography } from '../custom/CustomTypography';
import styles from './ExperimentalFeatures.module.css';
import {
  useGetCatalogEnabledQuery,
  useUpdateCatalogEnabledMutation,
} from 'src/redux/services/settings';
import { useGetOrgLkSelfConnectEnabledQuery, useUpdateOrgLkSelfConnectEnabledMutation } from 'src/redux/services/organization';

interface IFeatures {
  id: number;
  name: string;
  description: string;
  roles: Roles[];
}

const mapStateToProps = ({ user }: RootState) => ({
  roleInApp: user.roleInApp,
});

interface TExperimentalFeaturesComponent {
  roleInApp: TUserSlice['roleInApp'];
}

export const ExperimentalFeaturesComponent: FC<TExperimentalFeaturesComponent> = ({
  roleInApp,
}) => {
  const [stateFeatures, setStateFeatures] = useState<{ [name: string]: boolean }>({});
  const { data: cardsEnabled, refetch: cardsRefetch } = useGetCardsEnabledQuery();
  const [updateCardsEnabled] = useUpdateCardsEnabledMutation();
  const { data: catalogEnabled, refetch: catalogRefetch } = useGetCatalogEnabledQuery();
  const [updateCatalogEnabled] = useUpdateCatalogEnabledMutation();
  const { data: orgLkSelfConnectEnabled, refetch: orgLkSelfConnectRefetch } =
    useGetOrgLkSelfConnectEnabledQuery();
  const [updateOrgLkSelfConnectEnabled] = useUpdateOrgLkSelfConnectEnabledMutation();

  // Список функций доступных в экспериментальных настройках
  const features: IFeatures[] = [
    {
      id: 1,
      name: 'Визитки',
      description:
        'Активирует возможность использования визиток в приложении. В профиле каждого пользователя появляется соответствующий раздел. Визитка пользователя доступна по адресу api/cards/{userId}.',
      roles: [Roles.EDITOR, Roles.OWNER],
    },
    {
      id: 2,
      name: 'Каталог',
      description:
        'Активирует возможность использования каталога публичных приложений. Каждое приложение можно добавить в каталог для более удобного и быстрого доступа.',
      roles: [Roles.EDITOR, Roles.OWNER],
    },
    {
      id: 3,
      name: 'Самостоятельное подключение ЛК',
      description:
        'Позволяет пользователю самостоятельно подключить личный кабинет организации без обращения к администратору.',
      roles: [Roles.EDITOR, Roles.OWNER],
    },
  ];

  useEffect(() => {
    if (cardsEnabled !== undefined || catalogEnabled !== undefined || orgLkSelfConnectEnabled !== undefined) {
      setStateFeatures({
        Визитки: cardsEnabled || false,
        Каталог: catalogEnabled || false,
        'Самостоятельное подключение ЛК': orgLkSelfConnectEnabled || false,
      });
    }
  }, [cardsEnabled, catalogEnabled, orgLkSelfConnectEnabled]);

  const handleSwitchChange = async (name: string, checked: boolean) => {
    setStateFeatures((prev) => ({ ...prev, [name]: checked }));

    if (name === 'Визитки') {
      await updateCardsEnabled(checked).unwrap();
      cardsRefetch(); // Сбрасываем кеш и получаем актуальные данные
    }

    if (name === 'Каталог') {
      await updateCatalogEnabled(checked).unwrap();
      catalogRefetch(); // Сбрасываем кеш и получаем актуальные данные
    }

    if (name === 'Самостоятельное подключение ЛК') {
      await updateOrgLkSelfConnectEnabled(checked).unwrap();
      orgLkSelfConnectRefetch(); // Сбрасываем кеш и получаем актуальные данные
    }
  };

  return (
    <div className="wrapper-scroll">
      <div className="content">
        <h1>Experimental Features</h1>
        <p>Раздел экспериментальных настроек сервиса</p>
        <div>
          {features.map((item) => (
            <div key={item.id} className={styles.feature}>
              <div>
                <CustomTypography className={clsx('text-14')}>{item.name}</CustomTypography>
                <CustomTypography className={clsx('text-12')} color="grey">
                  {item.description}
                </CustomTypography>
              </div>
              <Switch
                checked={stateFeatures[item.name] || false}
                onChange={(e) => handleSwitchChange(item.name, e.target.checked)}
                disabled={roleInApp ? !item.roles.includes(roleInApp) : true}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ExperimentalFeatures = connect(mapStateToProps)(ExperimentalFeaturesComponent);
